// Path: frontend/src/pages/admin/components/chat-records/SlideChatRecordsTab.tsx

import React, { useState, useEffect, useMemo } from 'react';
import {
  getUserChatRecords,
  type UserChatRecord,
  type LessonChatRecord,
  type SlideMessage
} from '../../../../lib/apis_chatmsg';
import { api } from '../../../../lib/api';
import ChatRecordsFilter, { type FilterData, type FilterValues, type FilterOption } from './ChatRecordsFilter';
import ChatRecordsResult, { type BaseLessonRecord, type GroupedCourse } from './ChatRecordsResult';
import SlideMessagesTable from './SlideMessagesTable';

interface Course {
  id: number;
  title: string;
  stages?: Stage[];
}

interface Stage {
  id: number;
  title: string;
  course_id: number;
  lessons?: Lesson[];
}

interface Lesson {
  id: number;
  title: string;
  stage_id: number;
}

interface UserOption {
  id: number;
  username: string;
}

const getTodayDate = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDateDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const SlideChatRecordsTab: React.FC = () => {
  const [users, setUsers] = useState<FilterOption[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);

  const [filterValues, setFilterValues] = useState<FilterValues>({
    userId: '',
    courseId: '',
    stageId: '',
    lessonId: '',
    startDate: getDateDaysAgo(7),
    endDate: getTodayDate()
  });

  const [chatRecords, setChatRecords] = useState<UserChatRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const stages = useMemo((): FilterOption[] => {
    if (!filterValues.courseId) return [];
    const course = courses.find(c => c.id === filterValues.courseId);
    return course?.stages?.map(s => ({ id: s.id, title: s.title })) || [];
  }, [filterValues.courseId, courses]);

  const lessons = useMemo((): FilterOption[] => {
    if (!filterValues.stageId) return [];
    const course = courses.find(c => c.id === filterValues.courseId);
    const stage = course?.stages?.find(s => s.id === filterValues.stageId);
    return stage?.lessons?.map(l => ({ id: l.id, title: l.title })) || [];
  }, [filterValues.stageId, courses, filterValues.courseId]);

  const filterData: FilterData = useMemo(() => ({
    users,
    courses: courses.map(c => ({ id: c.id, title: c.title })),
    stages,
    lessons
  }), [users, courses, stages, lessons]);

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    if (!isLoadingFilters) {
      handleSearch();
    }
  }, [isLoadingFilters]);

  useEffect(() => {
    setFilterValues(prev => ({ ...prev, stageId: '', lessonId: '' }));
  }, [filterValues.courseId]);

  useEffect(() => {
    setFilterValues(prev => ({ ...prev, lessonId: '' }));
  }, [filterValues.stageId]);

  const fetchFilterData = async () => {
    setIsLoadingFilters(true);
    try {
      const [usersRes, coursesRes] = await Promise.all([
        api.get<UserOption[]>('/users'),
        api.get<Course[]>('/courses')
      ]);
      setUsers(usersRes.data.map(u => ({ id: u.id, title: u.username })));
      setCourses(coursesRes.data);
    } catch (error) {
      console.error('Failed to fetch filter data:', error);
    } finally {
      setIsLoadingFilters(false);
    }
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setHasSearched(true);
    try {
      const params: Record<string, any> = {};
      if (filterValues.startDate) params.start_date = filterValues.startDate;
      if (filterValues.endDate) params.end_date = filterValues.endDate;
      if (filterValues.userId) params.user_id = filterValues.userId;
      if (filterValues.courseId) params.course_id = filterValues.courseId;
      if (filterValues.stageId) params.stage_id = filterValues.stageId;
      if (filterValues.lessonId) params.lesson_id = filterValues.lessonId;

      const data = await getUserChatRecords(params);
      setChatRecords(data);
    } catch (error) {
      console.error('Failed to fetch chat records:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setFilterValues({
      userId: '',
      courseId: '',
      stageId: '',
      lessonId: '',
      startDate: getDateDaysAgo(7),
      endDate: getTodayDate()
    });
    setChatRecords([]);
    setHasSearched(false);
  };

  const handleValueChange = (key: keyof FilterValues, value: string | number | '') => {
    setFilterValues(prev => ({ ...prev, [key]: value }));
  };

  const countMessages = (user: UserChatRecord): number => {
    return user.lessons.reduce((total, lesson) => {
      return total + lesson.plans.reduce((pTotal, plan) => {
        return pTotal + plan.slide_messages.length;
      }, 0);
    }, 0);
  };

  const totalMessages = chatRecords.reduce((sum, user) => sum + countMessages(user), 0);

  const getLessons = (user: UserChatRecord): BaseLessonRecord[] => {
    return user.lessons;
  };

  const renderLessonMessages = (lesson: BaseLessonRecord, lessonKey: string): React.ReactNode => {
    const fullLesson = chatRecords
      .flatMap(u => u.lessons)
      .find(l => l.lesson_id === lesson.lesson_id);
    
    if (!fullLesson) return null;

    const allMessages: SlideMessage[] = fullLesson.plans.flatMap(plan =>
      plan.slide_messages.map(msg => ({
        ...msg,
        plan_id: plan.plan_id,
        version_number: plan.version_number
      }))
    );

    return <SlideMessagesTable messages={allMessages} />;
  };

  return (
    <>
      <ChatRecordsFilter
        filterData={filterData}
        values={filterValues}
        isLoading={isLoadingFilters}
        onValueChange={handleValueChange}
        onSearch={handleSearch}
        onReset={handleReset}
      />
      <ChatRecordsResult
        records={chatRecords}
        isLoading={isLoading}
        hasSearched={hasSearched}
        totalMessages={totalMessages}
        countMessages={countMessages}
        getLessons={getLessons}
        renderLessonMessages={renderLessonMessages}
      />
    </>
  );
};

export default SlideChatRecordsTab;
